import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import { Loader2, Plus, Trash2, Search, ClipboardList } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const api = axios.create({ baseURL: `${API_URL}/api/labs-store` });
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('vendor_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

export default function VendorBookForDoctorTab({ catalog }) {
  const [doctorSearch, setDoctorSearch] = useState('');
  const [doctors, setDoctors] = useState([]);
  const [doctorsError, setDoctorsError] = useState(false);
  const [doctorId, setDoctorId] = useState('');
  const [patientName, setPatientName] = useState('');
  const [patientPhone, setPatientPhone] = useState('');
  const [patientAge, setPatientAge] = useState('');
  const [patientGender, setPatientGender] = useState('');
  const [items, setItems] = useState([{ item_id: '', quantity: 1 }]);
  const [discountGiven, setDiscountGiven] = useState(0);
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [bookings, setBookings] = useState([]);
  const [loadingBookings, setLoadingBookings] = useState(true);
  const [testFilter, setTestFilter] = useState('');

  const searchDoctors = useCallback(async () => {
    setDoctorsError(false);
    try {
      const res = await api.get(`/vendor/doctors?search=${encodeURIComponent(doctorSearch)}`);
      setDoctors(res.data);
    } catch (err) {
      setDoctorsError(true);
      setDoctors([]);
    }
  }, [doctorSearch]);

  const fetchBookings = useCallback(async () => {
    setLoadingBookings(true);
    try {
      const res = await api.get('/vendor/my-bookings');
      setBookings(res.data);
    } catch (err) {
      toast.error('Failed to load your bookings. Please retry.');
      setBookings([]);
    } finally {
      setLoadingBookings(false);
    }
  }, []);

  // Debounce doctor search so we don't fire an API call on every keystroke
  useEffect(() => {
    const timer = setTimeout(() => { searchDoctors(); }, 300);
    return () => clearTimeout(timer);
  }, [searchDoctors]);
  useEffect(() => { fetchBookings(); }, [fetchBookings]);

  const filteredCatalog = testFilter
    ? catalog.filter((c) => c.name.toLowerCase().includes(testFilter.toLowerCase()))
    : catalog;

  const addItemRow = () => setItems([...items, { item_id: '', quantity: 1 }]);
  const removeItemRow = (idx) => setItems(items.filter((_, i) => i !== idx));
  const updateItemRow = (idx, field, value) => {
    const copy = [...items];
    copy[idx][field] = value;
    setItems(copy);
  };

  const selectedTests = items.map((it) => catalog.find((c) => c.id === it.item_id)).filter(Boolean);
  const b2bTotal = items.reduce((sum, it) => {
    const t = catalog.find((c) => c.id === it.item_id);
    return sum + (t ? t.b2b_price * it.quantity : 0);
  }, 0);
  const patientMrpTotal = items.reduce((sum, it) => {
    const t = catalog.find((c) => c.id === it.item_id);
    return sum + (t ? (t.patient_mrp || t.mrp) * it.quantity : 0);
  }, 0);
  const doctorEarning = Math.round((patientMrpTotal - discountGiven - b2bTotal) * 100) / 100;

  const resetForm = () => {
    setDoctorId(''); setPatientName(''); setPatientPhone(''); setPatientAge(''); setPatientGender('');
    setItems([{ item_id: '', quantity: 1 }]); setDiscountGiven(0); setNotes('');
  };

  const submitBooking = async () => {
    if (!doctorId) { toast.error('Please select a doctor'); return; }
    if (!patientName || !patientPhone) { toast.error('Patient name and phone are required'); return; }
    if (!items.every((it) => it.item_id)) { toast.error('Please select a test for every row'); return; }

    setSubmitting(true);
    try {
      const res = await api.post('/vendor/book-for-doctor', {
        doctor_id: doctorId,
        patient_name: patientName,
        patient_phone: patientPhone,
        patient_age: patientAge,
        patient_gender: patientGender,
        items: items.map((it) => ({ item_id: it.item_id, quantity: it.quantity })),
        discount_given: parseFloat(discountGiven) || 0,
        notes,
      });
      toast.success(`Booking created. ${res.data.payment_status === 'paid' ? 'Charged from doctor wallet.' : 'Marked due for doctor.'}`);
      resetForm();
      fetchBookings();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create booking');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="vendor-book-for-doctor-tab">
      <Card>
        <CardHeader>
          <CardTitle>Book Test On Behalf Of Doctor</CardTitle>
          <CardDescription>Select the doctor, patient, and tests. Doctor is charged the B2B price automatically.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>Search Doctor</Label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input className="pl-9" placeholder="Search by name, clinic, or email" value={doctorSearch} onChange={(e) => setDoctorSearch(e.target.value)} data-testid="book-for-doctor-search" />
            </div>
            <Select value={doctorId} onValueChange={setDoctorId}>
              <SelectTrigger className="mt-2" data-testid="book-for-doctor-select"><SelectValue placeholder={doctorsError ? 'Failed to load doctors' : 'Select a doctor'} /></SelectTrigger>
              <SelectContent>
                {doctors.map((d) => <SelectItem key={d.id} value={d.id}>{d.name} {d.clinic_name ? `(${d.clinic_name})` : ''}</SelectItem>)}
              </SelectContent>
            </Select>
            {doctorsError && (
              <p className="text-xs text-red-500 mt-1 flex items-center gap-2">
                Failed to load doctors.
                <button type="button" className="underline" onClick={searchDoctors} data-testid="book-for-doctor-retry">Retry</button>
              </p>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div><Label>Patient Name</Label><Input value={patientName} onChange={(e) => setPatientName(e.target.value)} data-testid="book-for-doctor-patient-name" /></div>
            <div><Label>Patient Phone</Label><Input value={patientPhone} onChange={(e) => setPatientPhone(e.target.value)} data-testid="book-for-doctor-patient-phone" /></div>
            <div><Label>Patient Age</Label><Input value={patientAge} onChange={(e) => setPatientAge(e.target.value)} data-testid="book-for-doctor-patient-age" /></div>
          </div>

          <div>
            <Label>Tests</Label>
            <Input
              className="mb-2"
              placeholder="Type to filter tests..."
              value={testFilter}
              onChange={(e) => setTestFilter(e.target.value)}
              data-testid="book-for-doctor-test-filter"
            />
            <div className="space-y-2">
              {items.map((it, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <Select value={it.item_id} onValueChange={(v) => updateItemRow(idx, 'item_id', v)}>
                    <SelectTrigger className="flex-1" data-testid={`book-for-doctor-test-select-${idx}`}><SelectValue placeholder="Select test" /></SelectTrigger>
                    <SelectContent>
                      {filteredCatalog.map((c) => <SelectItem key={c.id} value={c.id}>{c.name} (₹{c.b2b_price} B2B / ₹{c.patient_mrp || c.mrp} MRP)</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Input type="number" min={1} className="w-20" value={it.quantity} onChange={(e) => updateItemRow(idx, 'quantity', Math.max(1, parseInt(e.target.value) || 1))} data-testid={`book-for-doctor-qty-${idx}`} />
                  <Button variant="ghost" size="icon" onClick={() => removeItemRow(idx)} disabled={items.length === 1}><Trash2 className="w-4 h-4 text-red-500" /></Button>
                </div>
              ))}
              <Button variant="outline" size="sm" onClick={addItemRow} data-testid="book-for-doctor-add-test"><Plus className="w-4 h-4 mr-1" /> Add Test</Button>
            </div>
          </div>

          <div><Label>Discount Given To Patient (₹)</Label><Input type="number" min={0} value={discountGiven} onChange={(e) => setDiscountGiven(e.target.value)} data-testid="book-for-doctor-discount" /></div>
          <div><Label>Notes (optional)</Label><Input value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="book-for-doctor-notes" /></div>

          {selectedTests.length > 0 && (
            <div className="grid grid-cols-3 gap-2 bg-slate-50 rounded-lg p-3 text-sm">
              <div><p className="text-slate-400 text-xs">B2B Total (Doctor Pays)</p><p className="font-semibold">₹{b2bTotal.toFixed(2)}</p></div>
              <div><p className="text-slate-400 text-xs">Patient MRP Total</p><p className="font-semibold">₹{patientMrpTotal.toFixed(2)}</p></div>
              <div><p className="text-slate-400 text-xs">Doctor Earning</p><p className="font-semibold text-teal-700">₹{doctorEarning.toFixed(2)}</p></div>
            </div>
          )}

          <Button className="w-full" onClick={submitBooking} disabled={submitting} data-testid="book-for-doctor-submit">
            {submitting && <Loader2 className="w-4 h-4 animate-spin mr-2" />} Create Booking
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="flex items-center gap-2"><ClipboardList className="w-4 h-4" /> My Bookings</CardTitle></CardHeader>
        <CardContent>
          {loadingBookings ? (
            <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-teal-600" /></div>
          ) : bookings.length === 0 ? (
            <p className="text-sm text-slate-400 text-center py-6">No bookings created yet.</p>
          ) : (
            <div className="space-y-2">
              {bookings.map((b) => (
                <div key={b.id} className="flex items-center justify-between p-3 border rounded-lg" data-testid={`vendor-booking-row-${b.id}`}>
                  <div>
                    <p className="font-medium text-sm">{b.order_number} • {b.patient_name}</p>
                    <p className="text-xs text-slate-400">For {b.doctor_name?.startsWith('Dr.') ? b.doctor_name : `Dr. ${b.doctor_name}`} • {new Date(b.created_at).toLocaleDateString()}</p>
                  </div>
                  <Badge className={b.payment_status === 'due' ? 'bg-amber-100 text-amber-700' : 'bg-green-100 text-green-700'}>
                    {b.payment_status === 'due' ? `Due ₹${b.due_amount}` : 'Paid'}
                  </Badge>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
